import {
  Component,
  OnInit,
  Input,
  OnDestroy,
  ChangeDetectorRef,
} from "@angular/core";
import { Router, NavigationEnd, Event as RouterEvent } from "@angular/router";
import { distance } from "fastest-levenshtein";
import { lastValueFrom, Observable, of, Subscription } from "rxjs";
import { filter, map, distinctUntilChanged, startWith } from "rxjs/operators";
import { SideNavigatorService } from "../../services/side-navigator.service";
import { ScreenSizeService } from "src/app/services/screen-size.service";
import { FeatureService } from "src/app/services/features.service";
import { AuthService } from "../../services/auth.service";
import { CacheService } from "src/app/services/cache.service";
import { NetworkService } from "src/app/services/network.service";

@Component({
  selector: "app-sidebar",
  templateUrl: "./sidebar.component.html",
  styleUrls: ["./sidebar.component.scss"],
})
export class SidebarComponent implements OnInit, OnDestroy {
  @Input() docked: boolean = false; // Accept docked input parameter

  networkSubscription: Subscription | null = null;
  online = true;
  routerEventsSubscription: Subscription | null = null;

  userInfo$: Observable<any>;
  org$: Observable<any>;

  features = [];
  orderedFeatures = [];
  currentFeature: any;
  isMobile: boolean;
  isDesktop: boolean;
  public featureIconUrlMap: { [key: string]: string } = {};
  // Always computed from current route
  get isLoginRoute(): boolean {
    return this.router.url.startsWith("/login");
  }

  constructor(
    public auth: AuthService,
    public sideNavigatorService: SideNavigatorService,
    public screenSizeService: ScreenSizeService,
    public featureService: FeatureService,
    private router: Router,
    private cacheService: CacheService,
    private networkService: NetworkService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // No need to set isLoginRoute; it is computed from Router URL
    this.fetchUserData();

    // Initialize features initially
    this.initializeFeatures();

    // Listen to network status changes
    this.networkSubscription = this.networkService
      .getOnlineStatus()
      .subscribe((isOnline) => {
        console.log("Network status event:", isOnline);
        if (this.online !== isOnline) {
          console.log("Network status changed:", isOnline);
          this.online = isOnline;
          this.initializeFeatures();
        }
      });

    // Listen to screen size changes
    this.screenSizeService.isMobile$.subscribe((isMobile) => {
      this.isMobile = isMobile;
    });

    this.screenSizeService.isDesktop$.subscribe((isDesktop) => {
      this.isDesktop = isDesktop;
    });

    // Subscribe to Router NavigationEnd events
    this.routerEventsSubscription = this.router.events
      .pipe(filter((event: RouterEvent) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        console.log("NavigationEnd event:", event);
        this.updateCurrentFeature(event.urlAfterRedirects);
      });

    // Trigger refresh when sidebar becomes visible due to route change (leaving login)
    const routeVisibilitySub = this.router.events
      .pipe(
        startWith(null as any), // evaluate once on init
        map(() => this.isLoginRoute),
        distinctUntilChanged()
      )
      .subscribe((isLogin) => {
        if (!isLogin) {
          // Sidebar visible by route -> ensure it's hydrated
          this.ensureActiveState();
        }
      });

    // Trigger refresh when user explicitly shows the side navigator
    const sideNavVisibilitySub = this.sideNavigatorService.hideSideNav$
      .pipe(
        distinctUntilChanged(),
        map((hidden) => !hidden)
      )
      .subscribe((visible) => {
        if (visible && !this.isLoginRoute) {
          this.ensureActiveState();
        }
      });

    // Track additional subs for cleanup
    if (this.routerEventsSubscription) {
      (this.routerEventsSubscription as any).add(routeVisibilitySub);
      (this.routerEventsSubscription as any).add(sideNavVisibilitySub);
    }
  }

  hideAndLogout(): void {
    try {
      this.sideNavigatorService.hide();
    } catch (e) {
      console.warn("Failed to hide sidebar before logout", e);
    }
    // Reset any sidebar-bound state before navigating away
    this.resetSidebarState();
    this.auth.logout();
  }

  private resetSidebarState(): void {
    try {
      // Clear streams consumed by the template
      this.userInfo$ = of(null);
      this.org$ = of(null);

      // Clear local UI state
      this.features = [];
      this.orderedFeatures = [];
      this.currentFeature = null;
      this.featureIconUrlMap = {};
      // isLoginRoute is computed; no manual assignment necessary

      // Unsubscribe from live streams
      if (this.networkSubscription) {
        this.networkSubscription.unsubscribe();
        this.networkSubscription = null;
      }
      // Keep routerEventsSubscription so we can re-evaluate when route changes

      // Trigger UI update
      try {
        this.cdr.detectChanges();
      } catch {}
    } catch (err) {
      console.warn("Error resetting sidebar state on logout", err);
    }
  }

  // Ensure sidebar data and subscriptions are active when not on login
  private ensureActiveState(): void {
    // Re-fetch user/org
    this.fetchUserData();
    // Re-initialize features
    this.initializeFeatures();
    // Re-create network subscription if needed
    if (!this.networkSubscription) {
      this.networkSubscription = this.networkService
        .getOnlineStatus()
        .subscribe((isOnline) => {
          console.log("Network status event:", isOnline);
          if (this.online !== isOnline) {
            console.log("Network status changed:", isOnline);
            this.online = isOnline;
            this.initializeFeatures();
          }
        });
    }
  }

  private async initializeFeatures(): Promise<void> {
    // Step 1: Fetch Feature Descriptions
    try {
      const featureData = await this.featureService.getFeatureDescriptions();
      // filter for only features that offline true if !this.networkService.isOnline()
      this.features = featureData.filter(
        (f) => f.offline === "true" || this.networkService.isOnline()
      );
      console.log("Feature Descriptions:", featureData);

      // Step 2: Determine the Current Feature
      const currentRoute = this.router.url;
      this.currentFeature = this.findBestMatchingFeature(currentRoute);

      // Step 3: Fetch User Information
      const userInfo = await this.auth.getUserInfo(); // Await the async result of getUserInfo
      // Step 4: Fetch Feature Order based on organization_id
      try {
        const orderList = await this.featureService.getFeatureOrder(
          userInfo.organization_id
        ); // Await feature order fetch
        console.log("Order List:", orderList);

        // Create a map of feature_id to order for quick lookup
        const orderMap = new Map<string, number>();
        if (orderList && Array.isArray(orderList["list"])) {
          orderList["list"].forEach((item) => {
            orderMap.set(item.feature_id, item.order);
          });
        }

        // Separate features into ordered and unordered arrays
        const orderedFeatures: any[] = [];
        const unorderedFeatures: any[] = [];

        this.features.forEach((feature) => {
          if (orderMap.has(feature.feature_id)) {
            orderedFeatures.push(feature);
          } else {
            unorderedFeatures.push(feature);
          }
        });

        // Sort orderedFeatures based on their specified order
        orderedFeatures.sort((a, b) => {
          return (
            (orderMap.get(a.feature_id) || 0) -
            (orderMap.get(b.feature_id) || 0)
          );
        });

        console.log("Unordered Features:", unorderedFeatures);

        // Sort unorderedFeatures alphabetically by homeName
        unorderedFeatures.sort((a, b) => a.homeName.localeCompare(b.homeName));

        // Combine both arrays, with orderedFeatures first
        this.orderedFeatures = [...orderedFeatures, ...unorderedFeatures];

        console.log("Ordered Features:", this.orderedFeatures);
      } catch (error) {
        console.error("Failed to fetch feature order:", error);
        // Fallback: Sort all features alphabetically by homeName
        this.features.sort((a, b) => a.homeName.localeCompare(b.homeName));
        this.orderedFeatures = [...this.features];
        console.log(
          "Features sorted alphabetically due to order fetch failure."
        );
      }
    } catch (error) {
      console.error("Failed to fetch feature descriptions:", error);
      // Optionally handle feature descriptions fetch failure
    }
  }

  fetchUserData(): void {
    // Fetch user information
    this.auth
      .getUserInfo()
      .then((userInfo) => {
        // Set the resolved user info as an observable
        this.userInfo$ = of(userInfo);
      })
      .catch((error) => {
        console.error("Error fetching user info:", error);
        // Handle error if needed
      });

    // Fetch organization data
    this.auth
      .getOrganization()
      .then((organization) => {
        // Set the resolved organization as an observable
        this.org$ = of(organization);
      })
      .catch((error) => {
        console.error("Error fetching organization:", error);
        // Handle error if needed
      });
  }

  ngOnDestroy(): void {
    if (this.networkSubscription) {
      this.networkSubscription.unsubscribe();
    }
    if (this.routerEventsSubscription) {
      this.routerEventsSubscription.unsubscribe();
    }
    // Unsubscribe from screen size observables if stored as subscriptions
    // (Assuming you stored them, otherwise manage accordingly)
  }

  navigateToHome(): void {
    this.router.navigate(["/home"]);
    this.currentFeature = this.findBestMatchingFeature("/home");
    this.sideNavigatorService.toggleSideNavigator();
  }

  navigateToFeature(featureId: string, path: string, queryParam: string): void {
    this.router.navigateByUrl("/", { skipLocationChange: true }).then(() => {
      this.router.navigate(["/react-native", path.replace(/\//g, "")], {
        queryParams: { navigationParam: queryParam, uuid: featureId },
      });
      this.currentFeature = this.findBestMatchingFeature(path);
      this.sideNavigatorService.toggleSideNavigator();
    });
  }

  async getFeatureIconUrl(feature: any): Promise<void> {
    const iconUrl = `${feature.baseUrl}${feature.homeIcon}`;

    let computedUrl: string = "";

    const img = await lastValueFrom(
      this.cacheService.getResource(iconUrl, true)
    );

    // now destructure into `data` and `content`
    const { data, contentType: content } = img;

    if (data) {
      if (typeof data === "string" && data.startsWith("data:image")) {
        computedUrl = data;
        // } else if (img instanceof Blob) {
        //   computedUrl = URL.createObjectURL(img);
      } else {
        computedUrl = data as string;
      }
    } else {
      computedUrl = "";
    }

    this.featureIconUrlMap[iconUrl] = computedUrl;
    // Notify Angular to update change detection
    // this.cdr.markForCheck();
  }

  getIcon(feature: any): string {
    const iconUrl = feature.baseUrl + feature.homeIcon;
    if (!this.featureIconUrlMap[iconUrl]) {
      // Trigger asynchronous loading if icon is not available yet
      this.getFeatureIconUrl(feature);
      return ""; // Return empty string until the icon URL is loaded
    }
    return this.featureIconUrlMap[iconUrl];
  }

  onImageError() {
    this.networkService
      .checkInternetConnection()
      .then((isConnected) => {
        if (isConnected) {
          console.log("Internet is active");
        } else {
          this.networkService.markInternetDown();
        }
      })
      .catch((error) =>
        console.error("Unexpected error in connectivity check:", error)
      );
  }

  findBestMatchingFeature(currentRoute: string): any {
    let bestMatch = null;
    let bestDistance = Infinity;

    this.features.forEach((feature) => {
      const dist = distance(
        currentRoute.replace(/\/react-native(\/|$)/, "/"),
        feature.defaultPath.replace(/\/react-native(\/|$)/, "/")
      );

      if (dist < bestDistance) {
        bestDistance = dist;
        bestMatch = feature;
      }
    });

    const dist = distance(currentRoute, "/home");
    if (dist < bestDistance) {
      bestDistance = dist;
      bestMatch = {
        name: "Home",
        defaultPath: "/home",
      };
    }

    return bestMatch;
  }

  findBestMatchingSubFeature(options): any {
    let bestMatch = null;
    let bestDistance = Infinity;
    const currentRoute = this.router.url;

    options.forEach((option) => {
      const dist = distance(currentRoute, option.name);

      if (dist < bestDistance) {
        bestDistance = dist;
        bestMatch = option;
      }
    });

    return bestMatch;
  }

  endsWithRoute(route: string): boolean {
    console.log("Route:", route);
    console.log("Current Route:", this.router.url);

    const currentRoute = this.router.url;
    const decodedRoute = decodeURIComponent(currentRoute);
    const searchString = `navigationParam=${route}`;

    return decodedRoute.includes(searchString);
  }

  filterFeaturesForScreenSize() {
    if (this.isDesktop) {
      // If we are on a desktop, return all features
      return this.orderedFeatures;
    }

    // If not on desktop, filter out features with desktopOnly = true
    return this.orderedFeatures.filter((feature) => !feature.desktopOnly);
  }

  // New method to update current feature based on navigation
  private updateCurrentFeature(newRoute: string): void {
    this.currentFeature = this.findBestMatchingFeature(newRoute);
    console.log("Updated Current Feature:", this.currentFeature);
  }
}
